import { useCreatePath, useDataProvider, useGetIdentity, useListContext, useNotify, useRefresh } from 'ra-core';
import { formatBRL, formatBrDateTime } from '@/lib/format';
import { List } from '@/components/admin/list';
import { Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
} from '@/components/ui/pagination';
import { ChevronLeftIcon, ChevronRightIcon, Archive, ArchiveRestore } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CabecalhoPagina, abasBentoGatilho, abasBentoLista } from '../bento/ui';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { generateProposalPdf, generateMultiCotasPdf, type ProposalPayload, type UserInfo, type MultiCotasData } from '../consortium-groups/proposal-utils';
import { PorQueConsorcioDialog } from '../consortium-groups/PorQueConsorcioDialog';
import { SaleModal } from '../consortium-groups/SaleModal';
import { CotaSelectionModal } from '../consortium-groups/CotaSelectionModal';
import { parseVagasDisponiveis, isGrupoQuaseCheio, SEM_VAGAS_MSG, POUCAS_VAGAS_MSG } from '../consortium-groups/sale-options';
import { supabase } from '@/components/atomic-crm/providers/supabase/supabase';
import { GrupoLink } from '../consortium-groups/GrupoLink';
import { CaixaPlaybook } from '../ai/playbook/CaixaPlaybook';
import { IconePlaybook } from '../ai/playbook/BotaoPlaybook';
import type { AlvoPlaybook } from '../ai/playbook/playbook';

type ProposalSimulationRecord = {
  id: string;
  contact_id: number;
  group_number?: number | null;
  category_code?: string | null;
  generated_at?: string | null;
  proposal_payload?: ProposalPayload & {
    resumo_projeto?: { credito_liquido_total?: string; credito_contratado_total?: string };
    cotas?: unknown[];
  };
  advisor_snapshot?: UserInfo;
  contact_snapshot?: { name?: string; phone?: string | null; email?: string | null };
  simulation_input?: {
    tipo?: string;
    clientName?: string;
    clientPhone?: string;
    clientEmail?: string;
    valorCredito?: number;
  };
  created_by?: string | null;
  archived_at?: string | null;
};

type SaleCategory = {
  id: string;
  admin_fee_pct?: number;
  categoria_code: string;
  categoria_nome: string | null;
  valor_credito: string | number | null;
  prazo: number | null;
  percentual_mensal: string | number | null;
  parcela_sem_seguro: string | number | null;
  valor_seguro: string | number | null;
  total_parcela: string | number | null;
  observacao: string | null;
  purchase_url: string | null;
};

type SaleModalState = {
  groupNumber: number;
  segment: 'M' | 'I';
  isFlex: boolean;
  category: SaleCategory;
  initialContactId: number;
  selectedCota: string | null;
  step: 'cota' | 'sale';
};

const formatCurrency = (value?: number) => {
  if (typeof value !== 'number') return '-';
  return formatBRL(value);
};

const PAGE_SIZE_OPTIONS = [25, 50, 100];

const range = (start: number, end: number) =>
  Array.from({ length: Math.max(0, end - start + 1) }, (_, i) => start + i);

// Compact page list with ellipsis for large page counts (1 … 4 5 6 … 12).
const buildPageList = (current: number, total: number): (number | 'ellipsis')[] => {
  if (total <= 7) return range(1, total);
  const pages: (number | 'ellipsis')[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) pages.push('ellipsis');
  pages.push(...range(start, end));
  if (end < total - 1) pages.push('ellipsis');
  pages.push(total);
  return pages;
};

// Client-side pager. Server pagination is disabled on this page because the
// search/creator filters run client-side over the full result set, so the page
// count has to reflect the *filtered* list — not the whole table.
const ClientPagination = ({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  return (
    <div className="flex items-center justify-end gap-4 flex-wrap">
      <div className="hidden md:flex items-center gap-2">
        <span className="text-sm text-muted-foreground">Resultados por página:</span>
        <Select value={String(pageSize)} onValueChange={(value) => onPageSizeChange(Number(value))}>
          <SelectTrigger className="h-8 w-[70px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent side="top">
            {PAGE_SIZE_OPTIONS.map((size) => (
              <SelectItem key={size} value={String(size)}>
                {size}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="text-sm text-muted-foreground">
        {start}-{end} de {total}
      </div>
      <Pagination className="mx-0 w-auto">
        <PaginationContent>
          <PaginationItem>
            <PaginationLink
              href="#"
              aria-label="Anterior"
              aria-disabled={page <= 1}
              tabIndex={page <= 1 ? -1 : 0}
              className={cn('gap-1 px-2.5', page <= 1 ? 'opacity-50 cursor-not-allowed' : '')}
              onClick={(event) => {
                event.preventDefault();
                if (page > 1) onPageChange(page - 1);
              }}
            >
              <ChevronLeftIcon />
            </PaginationLink>
          </PaginationItem>
          {buildPageList(page, totalPages).map((pageNumber, index) =>
            pageNumber === 'ellipsis' ? (
              <PaginationItem key={`ellipsis-${index}`}>
                <PaginationEllipsis />
              </PaginationItem>
            ) : (
              <PaginationItem key={pageNumber}>
                <PaginationLink
                  href="#"
                  isActive={pageNumber === page}
                  onClick={(event) => {
                    event.preventDefault();
                    onPageChange(pageNumber);
                  }}
                >
                  {pageNumber}
                </PaginationLink>
              </PaginationItem>
            ),
          )}
          <PaginationItem>
            <PaginationLink
              href="#"
              aria-label="Próximo"
              aria-disabled={page >= totalPages}
              tabIndex={page >= totalPages ? -1 : 0}
              className={cn('gap-1 px-2.5', page >= totalPages ? 'opacity-50 cursor-not-allowed' : '')}
              onClick={(event) => {
                event.preventDefault();
                if (page < totalPages) onPageChange(page + 1);
              }}
            >
              <ChevronRightIcon />
            </PaginationLink>
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  );
};

const ProposalSimulationsListContent = () => {
  const { data, isPending } = useListContext<ProposalSimulationRecord>();
  const createPath = useCreatePath();
  const { identity } = useGetIdentity();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  // `?q=` semeia a busca — é o que deixa uma linha do dashboard abrir esta
  // página já filtrada na pessoa clicada. Só o valor INICIAL: depois quem manda
  // é a caixa de busca, sem brigar com a URL.
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(() => searchParams.get('q') ?? '');
  const [creatorFilter, setCreatorFilter] = useState('all');
  const [tab, setTab] = useState<'abertas' | 'arquivadas'>('abertas');
  const [porQue, setPorQue] = useState<{ payload: ProposalPayload; userInfo: UserInfo } | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [regeneratingProposalId, setRegeneratingProposalId] = useState<string | null>(null);
  const [preparingSaleProposalId, setPreparingSaleProposalId] = useState<string | null>(null);
  const [archivingId, setArchivingId] = useState<string | null>(null);
  const [archivingAll, setArchivingAll] = useState(false);
  const [saleModalState, setSaleModalState] = useState<SaleModalState | null>(null);
  // Na tela, não na linha: trocar de página não fecha a caixinha.
  const [playbook, setPlaybook] = useState<AlvoPlaybook | null>(null);
  const isSuperAdmin = !!identity?.is_super_admin;
  const showCreator = isSuperAdmin || !!identity?.is_manager;
  // identity.id é o auth UUID (mesmo formato de created_by), então dá pra saber
  // se o usuário atual é o criador da proposta.
  const myAuthId = identity?.id != null ? String(identity.id) : null;
  // Só o criador (ou super-admin) arquiva — espelha a policy/guard no banco.
  const canArchive = (p: ProposalSimulationRecord) =>
    isSuperAdmin || (!!myAuthId && String(p.created_by ?? '') === myAuthId);

  const setArchived = async (proposal: ProposalSimulationRecord, archive: boolean) => {
    setArchivingId(proposal.id);
    try {
      await dataProvider.update('proposal_simulations', {
        id: proposal.id,
        data: {
          archived_at: archive ? new Date().toISOString() : null,
          archived_by: archive ? myAuthId : null,
        },
        previousData: proposal,
      });
      notify(archive ? 'Proposta arquivada' : 'Proposta desarquivada', { type: 'info' });
      refresh();
    } catch (error: any) {
      notify(`Não foi possível ${archive ? 'arquivar' : 'desarquivar'} a proposta: ${error?.message || 'erro desconhecido'}`, { type: 'error' });
    } finally {
      setArchivingId(null);
    }
  };

  const getCreatorLabel = (item: ProposalSimulationRecord) =>
    item.advisor_snapshot?.name || item.advisor_snapshot?.email || 'não identificado';

  const creatorOptions = useMemo(() => {
    if (!showCreator || !data?.length) return [];

    const labels = Array.from(new Set(data.map((item) => getCreatorLabel(item)).filter(Boolean)));
    return labels.sort((a, b) => a.localeCompare(b));
  }, [data, showCreator]);

  const filteredData = useMemo(() => {
    if (!data?.length) return [];
    const term = search.trim().toLowerCase();
    return data.filter((item) => {
      // Aba: "abertas" esconde arquivadas; "arquivadas" mostra só as arquivadas.
      const isArchived = item.archived_at != null;
      if (tab === 'arquivadas' ? !isArchived : isArchived) return false;

      if (showCreator && creatorFilter !== 'all') {
        const creator = getCreatorLabel(item);
        if (creator !== creatorFilter) return false;
      }

      if (!term) return true;

      const clientName = String(item.proposal_payload?.clientName || '').toLowerCase();
      const group = String(item.group_number || '').toLowerCase();
      const category = String(item.category_code || '').toLowerCase();
      const creatorName = String(item.advisor_snapshot?.name || '').toLowerCase();
      const creatorEmail = String(item.advisor_snapshot?.email || '').toLowerCase();

      return (
        clientName.includes(term)
        || group.includes(term)
        || category.includes(term)
        || creatorName.includes(term)
        || creatorEmail.includes(term)
      );
    });
  }, [data, search, showCreator, creatorFilter, tab]);

  // Propostas abertas (na aba atual) que o usuário pode arquivar — base do "arquivar tudo".
  // canArchive inlinado p/ deps estáveis (isSuperAdmin/myAuthId), sem recriar a função.
  const archivableInView = useMemo(
    () =>
      filteredData.filter(
        (p) => p.archived_at == null && (isSuperAdmin || (!!myAuthId && String(p.created_by ?? '') === myAuthId)),
      ),
    [filteredData, isSuperAdmin, myAuthId],
  );

  const archiveAll = async () => {
    if (archivableInView.length === 0) return;
    if (!window.confirm(`Arquivar ${archivableInView.length} proposta(s) desta lista?`)) return;
    setArchivingAll(true);
    let ok = 0;
    try {
      for (const proposal of archivableInView) {
        await dataProvider.update('proposal_simulations', {
          id: proposal.id,
          data: { archived_at: new Date().toISOString(), archived_by: myAuthId },
          previousData: proposal,
        });
        ok += 1;
      }
      notify(`${ok} proposta(s) arquivada(s)`, { type: 'info' });
      refresh();
    } catch (error: any) {
      notify(`Arquivadas ${ok}/${archivableInView.length}. Erro: ${error?.message || 'erro desconhecido'}`, { type: 'error' });
      refresh();
    } finally {
      setArchivingAll(false);
    }
  };

  // Filtering happens client-side, so any filter change has to send the user
  // back to page 1 — otherwise they'd be stranded on a now-out-of-range page.
  useEffect(() => {
    setPage(1);
  }, [search, creatorFilter, pageSize, tab]);

  const totalFiltered = filteredData.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));
  const safePage = Math.min(page, totalPages);
  const paginatedData = useMemo(
    () => filteredData.slice((safePage - 1) * pageSize, safePage * pageSize),
    [filteredData, safePage, pageSize],
  );

  if (isPending) {
    return <div className="p-4 text-sm text-muted-foreground">Carregando propostas...</div>;
  }

  const archivedCount = (data ?? []).filter((p) => p.archived_at != null).length;
  const openCount = (data?.length ?? 0) - archivedCount;

  // Vender e Gerar novamente: a mesma lógica que ficava no onClick de cada
  // cartão, só tirada da marcação para a linha da tabela caber na tela.
  const iniciarVenda = async (proposal: ProposalSimulationRecord) => {
    const groupNumber = Number(proposal.group_number);
    const categoryCode = proposal.category_code;

    if (!Number.isFinite(groupNumber) || !categoryCode) {
      notify('A proposta não possui grupo/categoria válidos para iniciar venda.', { type: 'warning' });
      return;
    }

    setPreparingSaleProposalId(proposal.id);
    try {
      const proposalPrazo = (proposal.proposal_payload as any)?.prazo as number | undefined;

      let categoryQuery = supabase
        .from('servopa_group_categories')
        .select('*')
        .eq('group_number', groupNumber)
        .eq('categoria_code', categoryCode)
        .order('sequence', { ascending: true });
      if (typeof proposalPrazo === 'number') {
        categoryQuery = categoryQuery.eq('prazo', proposalPrazo);
      }

      const [categoryResult, fallbackResult, groupMetaResult, groupDetailsResult] = await Promise.all([
        categoryQuery.limit(1),
        // Fallback: same group/category, any prazo — only used if the strict
        // query above returns nothing (e.g. proposta antiga ou sync incompleto).
        supabase
          .from('servopa_group_categories')
          .select('*')
          .eq('group_number', groupNumber)
          .eq('categoria_code', categoryCode)
          .order('sequence', { ascending: true })
          .limit(1),
        supabase
          .from('servopa_groups_enriched')
          .select('segment, flex, vacancies')
          .eq('group_number', groupNumber)
          .maybeSingle(),
        supabase
          .from('servopa_group_details')
          .select('numero_consorciados')
          .eq('group_number', groupNumber)
          .maybeSingle(),
      ]);

      if (categoryResult.error) throw categoryResult.error;
      if (fallbackResult.error) throw fallbackResult.error;
      if (groupMetaResult.error) throw groupMetaResult.error;

      // Trava de vagas: com zero confirmado nem abrimos o fluxo de
      // venda — vagas desconhecidas (grupo fora da enriched) passam.
      const vagas = parseVagasDisponiveis(groupMetaResult.data?.vacancies);
      if (vagas === 0) {
        notify(SEM_VAGAS_MSG, { type: 'warning' });
        return;
      }
      // Com <=10% das vagas restantes a venda segue, mas avisamos que
      // sem seguro pode falhar. Erro nesta query só suprime o aviso.
      if (isGrupoQuaseCheio(vagas, groupDetailsResult.data?.numero_consorciados)) {
        notify(POUCAS_VAGAS_MSG, { type: 'warning' });
      }

      let category =
        (categoryResult.data?.[0] as SaleCategory | undefined)
        ?? (fallbackResult.data?.[0] as SaleCategory | undefined);

      if (!category) {
        // Sintetiza a categoria a partir do payload da proposta, pra evitar
        // bloquear a venda quando a tabela de categorias não tem o (grupo, código)
        // exato — ex.: grupo desativado, sync defasado, código renomeado.
        const payload = proposal.proposal_payload as any;
        const parcelaSemSeguro = Number(payload?.parcelaSemSeguro ?? 0);
        const valorSeguro = Number(payload?.valorSeguro ?? 0);
        const valorCredito = Number(payload?.valorCredito ?? 0);
        const prazo = Number(payload?.prazo ?? 0);
        const adminFee = Number(payload?.taxaAdmin ?? 0);

        if (!valorCredito || !prazo) {
          notify('Categoria não encontrada e proposta sem dados completos para iniciar venda.', { type: 'warning' });
          return;
        }

        category = {
          id: `proposal-${proposal.id}`,
          admin_fee_pct: adminFee || undefined,
          categoria_code: categoryCode,
          categoria_nome: null,
          valor_credito: valorCredito,
          prazo,
          percentual_mensal: null,
          parcela_sem_seguro: parcelaSemSeguro,
          valor_seguro: valorSeguro,
          total_parcela: parcelaSemSeguro + valorSeguro,
          observacao: null,
          purchase_url: null,
        };
      }

      setSaleModalState({
        groupNumber,
        category,
        segment: groupMetaResult.data?.segment === 'I' ? 'I' : 'M',
        isFlex: Boolean(groupMetaResult.data?.flex),
        initialContactId: proposal.contact_id,
        selectedCota: null,
        step: 'cota',
      });
    } catch (error: any) {
      notify(`Não foi possível preparar a venda: ${error?.message || 'erro desconhecido'}`, { type: 'error' });
    } finally {
      setPreparingSaleProposalId(null);
    }
  };

  const gerarNovamente = async (proposal: ProposalSimulationRecord, isMultiCota: boolean) => {
    if (!proposal.proposal_payload) {
      notify('Esta proposta não tem dados salvos para gerar o PDF novamente.', { type: 'warning' });
      return;
    }

    setRegeneratingProposalId(proposal.id);
    try {
      if (isMultiCota && proposal.proposal_payload.cotas) {
        await generateMultiCotasPdf(
          proposal.proposal_payload as unknown as MultiCotasData,
          proposal.advisor_snapshot || {},
        );
      } else {
        await generateProposalPdf(
          proposal.proposal_payload,
          proposal.advisor_snapshot || {},
        );
      }
    } catch (error: any) {
      notify(`Não foi possível gerar o PDF: ${error?.message || 'erro desconhecido'}`, { type: 'error' });
    } finally {
      setRegeneratingProposalId(null);
    }
  };

  return (
    <div className="space-y-4 p-4">
      <PorQueConsorcioDialog
        payload={porQue?.payload ?? null}
        userInfo={porQue?.userInfo ?? {}}
        onClose={() => setPorQue(null)}
      />
      <CabecalhoPagina
        titulo="Propostas"
        descricao="Simulações geradas para fechamento ou arquivamento"
        acoes={
          tab === 'abertas' && archivableInView.length > 0 ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={archivingAll}
              onClick={archiveAll}
              title="Arquivar todas as propostas arquiváveis desta lista"
            >
              <Archive className="h-4 w-4 mr-1" />
              {archivingAll ? 'Arquivando...' : `Arquivar tudo (${archivableInView.length})`}
            </Button>
          ) : undefined
        }
      />

      {/* Lista numa tabela só (Premium North): abas, filtros e linhas no mesmo
          cartão. Nunca rola de lado: dados que andam juntos dividem a célula e,
          com menos de ~900px de conteúdo, cada proposta vira um bloco empilhado. */}
      <div className="cartao @container bg-card">
        <div className="px-4 pt-3">
          <div className={abasBentoLista}>
            {([['abertas', 'Abertas', openCount], ['arquivadas', 'Arquivadas', archivedCount]] as const).map(
              ([key, label, count]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setTab(key)}
                  data-state={tab === key ? 'active' : 'inactive'}
                  className={abasBentoGatilho}
                >
                  {label}
                  <span
                    className={cn(
                      'ml-1.5 rounded-full px-1.5 text-[11px] tabular-nums',
                      tab === key ? 'bg-ouro text-marinho' : 'bg-muted text-muted-foreground',
                    )}
                  >
                    {count}
                  </span>
                </button>
              ),
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 px-4 py-3">
          <Input
            className="h-9 w-full sm:w-80"
            placeholder="Buscar por cliente, grupo ou categoria"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />

          {showCreator && (
            <Select value={creatorFilter} onValueChange={setCreatorFilter}>
              <SelectTrigger className="h-9 w-full sm:w-64">
                <SelectValue placeholder="Filtrar por quem gerou" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os geradores</SelectItem>
                {creatorOptions.map((creator) => (
                  <SelectItem key={creator} value={creator}>
                    {creator}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {filteredData.length === 0 ? (
          <div className="border-t border-border px-4 py-8 text-center text-sm text-muted-foreground">
            Nenhuma proposta encontrada.
          </div>
        ) : (
          <div className="hidden border-y border-border px-4 py-2 text-xs font-medium text-muted-foreground @4xl:grid @4xl:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)_minmax(0,0.9fr)_minmax(0,1.4fr)] @4xl:gap-x-4">
            <span>Cliente</span>
            <span>Grupo · categoria</span>
            <span className="text-right">Crédito · telefone</span>
            <span className="text-right">Ações</span>
          </div>
        )}

        {paginatedData.map((proposal) => {
          const contactPath = createPath({
            resource: 'contacts',
            type: 'show',
            id: proposal.contact_id,
          });

          const isMultiCota = proposal.simulation_input?.tipo === 'projeto_estruturado'
            || Array.isArray(proposal.proposal_payload?.cotas);

          const clientName = proposal.proposal_payload?.clientName
            || proposal.contact_snapshot?.name
            || 'Cliente não informado';

          const telefone = String(
            proposal.proposal_payload?.clientPhone
            || proposal.contact_snapshot?.phone
            || '-',
          );

          const creditoDisplay = isMultiCota
            ? (proposal.proposal_payload?.resumo_projeto?.credito_contratado_total
                || proposal.proposal_payload?.resumo_projeto?.credito_liquido_total
                || '-')
            : formatCurrency(proposal.proposal_payload?.valorCredito);

          return (
            <div
              key={proposal.id}
              className="grid gap-x-4 gap-y-2 border-b border-[#EDF0F4] px-4 py-3 last:border-b-0 hover:bg-[#FAFBFC] @4xl:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)_minmax(0,0.9fr)_minmax(0,1.4fr)] @4xl:items-center"
            >
              <div className="min-w-0">
                <div className="text-sm font-semibold text-foreground">{clientName}</div>
                <div className="mt-0.5 text-[11.5px] text-muted-foreground">
                  {proposal.generated_at
                    ? formatBrDateTime(proposal.generated_at)
                    : 'data não informada'}
                  {showCreator && (
                    <> · por {getCreatorLabel(proposal)}</>
                  )}
                  {' · '}
                  <Link to={contactPath} className="text-primary underline-offset-2 hover:underline">
                    Ver contato
                  </Link>
                </div>
              </div>

              <div className="min-w-0 text-[13px]">
                <div className="font-medium">
                  <span className="text-muted-foreground font-normal">Grupo: </span>
                  {isMultiCota
                    ? 'Projeto estruturado'
                    : proposal.group_number ? (
                        <span className="font-mono"><GrupoLink groupNumber={proposal.group_number} /></span>
                      ) : '-'}
                </div>
                <div className="text-[12px] text-muted-foreground">
                  Categoria: <span className="font-medium text-foreground/80">{isMultiCota ? 'Multi-cotas' : (proposal.category_code || '-')}</span>
                </div>
              </div>

              <div className="min-w-0 text-[13px] @4xl:text-right">
                <div className="font-semibold tabular-nums text-foreground">
                  <span className="font-normal text-muted-foreground @4xl:hidden">Crédito: </span>
                  {creditoDisplay}
                </div>
                <div className="text-[12px] tabular-nums text-muted-foreground">
                  <span className="@4xl:hidden">Telefone: </span>
                  {telefone}
                </div>
              </div>

              <div className="flex flex-wrap gap-1.5 @4xl:justify-end">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setPlaybook({ tipo: 'consorcio', id: proposal.id, nome: clientName })}
                  title="Coach de vendas com IA para esta proposta"
                >
                  <IconePlaybook className="h-4 w-4 mr-1 text-primary" />
                  Playbook
                </Button>
                {!isMultiCota && (
                  <Button
                    type="button"
                    size="sm"
                    disabled={preparingSaleProposalId === proposal.id}
                    onClick={() => iniciarVenda(proposal)}
                  >
                    {preparingSaleProposalId === proposal.id ? 'Preparando...' : 'Vender'}
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={regeneratingProposalId === proposal.id}
                  onClick={() => gerarNovamente(proposal, isMultiCota)}
                >
                  {regeneratingProposalId === proposal.id ? 'Gerando...' : 'Gerar novamente'}
                </Button>
                {!isMultiCota && proposal.proposal_payload?.valorCredito && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setPorQue({
                      payload: proposal.proposal_payload as ProposalPayload,
                      userInfo: proposal.advisor_snapshot || {},
                    })}
                  >
                    Apresentação
                  </Button>
                )}
                {canArchive(proposal) && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={archivingId === proposal.id}
                    onClick={() => setArchived(proposal, proposal.archived_at == null)}
                    title={proposal.archived_at == null ? 'Arquivar proposta' : 'Desarquivar proposta'}
                  >
                    {proposal.archived_at == null ? (
                      <><Archive className="h-4 w-4 mr-1" />{archivingId === proposal.id ? 'Arquivando...' : 'Arquivar'}</>
                    ) : (
                      <><ArchiveRestore className="h-4 w-4 mr-1" />{archivingId === proposal.id ? 'Restaurando...' : 'Desarquivar'}</>
                    )}
                  </Button>
                )}
              </div>
            </div>
          );
        })}

        {totalFiltered > 0 && (
          <div className="border-t border-border px-4 py-2.5">
            <ClientPagination
              page={safePage}
              pageSize={pageSize}
              total={totalFiltered}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          </div>
        )}
      </div>

      {playbook && (
        <CaixaPlaybook key={String(playbook.id)} alvo={playbook} aoFechar={() => setPlaybook(null)} />
      )}

      <SaleModal
        open={saleModalState?.step === 'sale'}
        onClose={() => setSaleModalState(null)}
        groupNumber={saleModalState?.groupNumber || 0}
        category={saleModalState?.category || null}
        segment={saleModalState?.segment || 'M'}
        isFlex={saleModalState?.isFlex || false}
        selectedCota={saleModalState?.selectedCota || null}
        initialContactId={saleModalState?.initialContactId || null}
        adminFee={
          saleModalState?.category?.admin_fee_pct != null
            ? Number(saleModalState.category.admin_fee_pct)
            : null
        }
      />

      <CotaSelectionModal
        open={saleModalState?.step === 'cota'}
        onClose={() => setSaleModalState(null)}
        onContinue={(selectedCota) => {
          setSaleModalState((currentState) => {
            if (!currentState) return null;
            return {
              ...currentState,
              selectedCota,
              step: 'sale',
            };
          });
        }}
        groupNumber={saleModalState?.groupNumber || 0}
        category={saleModalState?.category || null}
        adminFee={saleModalState?.category?.admin_fee_pct ?? null}
        loading={false}
      />
    </div>
  );
};

export const ProposalSimulationsPage = () => {
  return (
    // Fetch the whole list in one page: the search + creator filters run
    // client-side, so we need every record available to filter and to build the
    // creator dropdown. Pagination is then handled client-side over the filtered
    // result (see ClientPagination), which is why the server pager is disabled.
    // storeKey={false} keeps a stale persisted perPage from shrinking the fetch.
    <List
      resource="proposal_simulations"
      sort={{ field: 'generated_at', order: 'DESC' }}
      perPage={1000}
      actions={false}
      title={false}
      pagination={false}
      storeKey={false}
      disableSyncWithLocation
    >
      <ProposalSimulationsListContent />
    </List>
  );
};
